import { useState } from "react";
import {
  Modal,
  Button,
  Alert,
  Upload,
  Progress,
  List,
  Typography,
  notification,
} from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import Papa from "papaparse";
import randomstring from "randomstring";
import { BaseUrl } from "../../Constants/Constants";
import UploadProgressModal from "./UploadProgressModal";

const BulkUpload = ({
  visible,
  onClose,
  CompanyOptions,
  TeamOptions,
  managerOptions,
  levels,
  domains,
  departments,
  onUploadComplete,
}) => {
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkTotal, setBulkTotal] = useState(0);
  const [bulkCurrent, setBulkCurrent] = useState(0);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkSuccessCount, setBulkSuccessCount] = useState(0);
  const [showBulkResults, setShowBulkResults] = useState(false);

  // Validation helper
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  // Password generation
  const generatePassword = () => {
    const length = 8;
    const alphanumeric =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const specialCharacters = "!@#$%&*()_+[]{}?";

    let passwordStr = "";
    passwordStr += randomstring.generate({
      length: 1,
      charset: "0123456789",
    });
    passwordStr += randomstring.generate({
      length: 1,
      charset: specialCharacters,
    });
    passwordStr += randomstring.generate({
      length: length - 2,
      charset: alphanumeric + specialCharacters,
    });

    return passwordStr
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  };

  // Email template creation
  const createEmailTemplate = (userName, email, password) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: Arial, sans-serif;
            background-color: #f4f4f4;
            margin: 0;
            padding: 0;
          }
          .container {
            background-color: #f4f4f4;
            margin: 20px auto;
            padding: 20px;
            max-width: 600px;
          }
          .card {
            background-color: #ffffff;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .header {
            background-color: #0073e6;
            padding: 10px;
            border-radius: 8px 8px 0 0;
            text-align: center;
            color: #ffffff;
          }
          .content {
            padding: 20px;
            text-align: center;
          }
          .content p {
            margin: 0;
            line-height: 1.5;
            color: #555555;
          }
          .button-container {
            margin: 20px 0;
            text-align: center;
          }
          .button {
            background-color: #0073e6;
            color: #ffffff !important;
            padding: 9px 20px;
            border-radius: 5px;
            border: 1px solid #0073e6;
            text-decoration: none;
            display: inline-block;
            transition: background-color 0.3s ease;
          }
          .button:hover {
            background-color: #ffffff;
            border: 1px solid #0073e6;
            color: #0073e6 !important;
          }
          .footer {
            padding: 10px;
            text-align: center;
            color: #777777;
            font-size: 12px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="card">
            <div class="header">
              <h1>Welcome to Training Nexus!</h1>
            </div>
            <div class="content">
              <p>Dear <strong>${userName}</strong>,</p>
              <p>Welcome to our digital learning platform! 🚀</p>
              <p>We're thrilled to have you onboard and are excited to help you unlock your full potential through our interactive courses.</p>
              <p>Your personalised access details are as follows:</p>
              <p><strong>Username:</strong> ${email.toLowerCase()}</p>
              <p><strong>Password:</strong> ${password}</p>
              <div class="button-container">
                <a href="https://training-nexus.valuehealthsolutions.com/login" class="button">Click here to Login</a>
              </div>
              <p>Thank you for registering in the portal to embark on your learning journey! Please reach out to <a href="mailto:srinivasan.s@valuehealthsol.com">srinivasan.s@valuehealthsol.com</a> for any questions and clarifications.</p>
            </div>
            <div class="footer">
              <p>Best regards,<br>Value Health Solutions</p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Download CSV template
  const downloadTemplate = () => {
    const companyNames = CompanyOptions.map((c) => c.label).join(" | ");
    const teamNames = TeamOptions.map((t) => t.label).join(" | ");
    const managerNames = managerOptions.map((m) => m.label).join(" | ");
    const levelsList = levels.join(" | ");
    const domainsList = domains.join(" | ");
    const departmentsList = departments.join(" | ");

    const csvContent = `employeeId,username,email,company,team,level,manager,domain,department,new_batch
VH001,John Doe,john.doe@valuehealthsolutions.com,${CompanyOptions[0]?.label || "VALUEHEALTHSOLUTIONS.COM"},${TeamOptions[0]?.label || "ENGINEERING TEAM"},Executive Management,${managerOptions[0]?.label || ""},Marketing,Development,false
VH002,Jane Smith,jane.smith@valuehealthsolutions.com,${CompanyOptions[0]?.label || "VALUEHEALTHSOLUTIONS.COM"},${TeamOptions[1]?.label || "MARKETING TEAM"},Core Team,${managerOptions[1]?.label || ""},Tech,Testing,true
VH003,Bob Johnson,bob.j@valuehealthsolutions.com,${CompanyOptions[0]?.label || "VALUEHEALTHSOLUTIONS.COM"},${TeamOptions[2]?.label || "SALES TEAM"},Entry Level,,HR,Devops,false,

# VALID VALUES (Copy these exactly):
# Companies: ${companyNames}
# Teams: ${teamNames}
# Managers: ${managerNames} (optional filed)
# Levels: ${levelsList}
# Domains: ${domainsList}
# Departments: ${departmentsList}
# new_batch: true | false`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "user_bulk_upload_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Main upload handler
  const handleBulkUpload = async () => {
    if (!bulkFile) return;

    setBulkUploading(true);
    setBulkErrors([]);
    setBulkSuccessCount(0);

    Papa.parse(bulkFile, {
      header: true,
      skipEmptyLines: true,
      comments: "#", 
      complete: async (results) => {
        const rows = results.data;
        const total = rows.length;

        setBulkTotal(total);
        setBulkCurrent(0);
        setBulkProgress(0);

        const errors = [];
        let successCount = 0;

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];

          try {
            setBulkCurrent(i + 1);

            // Validate required fields
            if (
              !row.username ||
              !row.email ||
              !row.employeeId ||
              !row.company ||
              !row.team ||
              !row.level ||
              !row.domain ||
              !row.department
            ) {
              errors.push({
                row: i + 1,
                email: row.email || "N/A",
                error: "Missing required fields",
              });
              continue;
            }

            // Validate email
            if (!validateEmail(row.email)) {
              errors.push({
                row: i + 1,
                email: row.email,
                error: "Invalid email format",
              });
              continue;
            }

            // Find Company
            const companyMatch = CompanyOptions.find((comp) => {
              const csvCompany = row.company.trim().toUpperCase();
              const dropdownCompany = comp.label.trim().toUpperCase();
              return csvCompany === dropdownCompany;
            });

            if (!companyMatch) {
              errors.push({
                row: i + 1,
                email: row.email,
                error: `Company "${row.company}" not found`,
              });
              continue;
            }

            // Find Team
            const teamMatch = TeamOptions.find((team) => {
              const csvTeam = row.team.trim().toUpperCase();
              const dropdownTeam = team.label.trim().toUpperCase();
              return csvTeam === dropdownTeam;
            });

            if (!teamMatch) {
              errors.push({
                row: i + 1,
                email: row.email,
                error: `Team "${row.team}" not found`,
              });
              continue;
            }

            // Find Manager (optional)
            let managerIdValue = null;
            if (row.manager && row.manager.trim() !== "") {
              const managerMatch = managerOptions.find((mgr) => {
                const csvManager = row.manager.trim().toLowerCase();
                const dropdownManager = mgr.label.trim().toLowerCase();
                return csvManager === dropdownManager;
              });

              if (managerMatch) {
                managerIdValue = managerMatch.value;
              } else {
                errors.push({
                  row: i + 1,
                  email: row.email,
                  error: `Manager "${row.manager}" not found`,
                });
                continue;
              }
            }

            // Validate level
            const validLevels = levels.map((l) => l.trim().toLowerCase());
            const csvLevel = row.level.trim();
            if (!validLevels.includes(csvLevel.toLowerCase())) {
              errors.push({
                row: i + 1,
                email: row.email,
                error: `Level "${row.level}" not valid`,
              });
              continue;
            }

            // Validate domain
            const validDomains = domains.map((d) => d.trim().toLowerCase());
            const csvDomain = row.domain.trim();
            if (!validDomains.includes(csvDomain.toLowerCase())) {
              errors.push({
                row: i + 1,
                email: row.email,
                error: `Domain "${row.domain}" not valid`,
              });
              continue;
            }

            // Validate department
            const validDepartments = departments.map((d) =>
              d.trim().toLowerCase()
            );
            const csvDepartment = row.department.trim();
            if (!validDepartments.includes(csvDepartment.toLowerCase())) {
              errors.push({
                row: i + 1,
                email: row.email,
                error: `Department "${row.department}" not valid`,
              });
              continue;
            }

            // Validate new_batch
            const validBatchValues = ["true", "false"];
            const csvBatch = row.new_batch.trim().toLowerCase();
            if (!validBatchValues.includes(csvBatch)) {
              errors.push({
                row: i + 1,
                email: row.email,
                error: `new_batch must be "true" or "false"`,
              });
              continue;
            }

            // Generate password
            const password = generatePassword();

            // Create email template
            const template = createEmailTemplate(
              row.username.trim(),
              row.email.trim(),
              password
            );

            // Send email
            await axios.post(
              `${BaseUrl}email-service`,
              {
                email: row.email.toLowerCase().trim(),
                subject: "Login Details for Training Nexus",
                template: template,
              },
              { withCredentials: true }
            );

            // Create user
            await axios.post(
              `${BaseUrl}auth/signup`,
              {
                userName: row.username.trim(),
                userPassword: password,
                Email: row.email.toLowerCase().trim(),
                IsActive: true,
                role: "user",
                companyid: companyMatch.value,
                teamid: teamMatch.value,
                employeeId: row.employeeId.trim(),
                level: csvLevel,
                domain: csvDomain,
                department: csvDepartment,
                new_batch: csvBatch === "true",
                manager: managerIdValue,
              },
              { withCredentials: true }
            );

            successCount++;
          } catch (err) {
            console.error(`Error processing row ${i + 1}:`, err);

            let errorMessage = "Unknown error";
            if (err.response?.data?.message) {
              errorMessage = err.response.data.message;
            } else if (err.response?.status === 409) {
              errorMessage = "User already exists";
            } else if (err.message) {
              errorMessage = err.message;
            }

            errors.push({
              row: i + 1,
              email: row.email || "N/A",
              error: errorMessage,
            });
          }

          // Update progress
          const current = i + 1;
          const progressPercentage = Math.round((current / total) * 100);
          setBulkProgress(progressPercentage);
        }

        // Processing complete - close progress modal, show results
        setBulkUploading(false); // This closes the progress modal
        setBulkSuccessCount(successCount);
        setBulkErrors(errors);
        setShowBulkResults(true); // This shows results in main modal

        if (successCount > 0) {
          onUploadComplete();
        }

        // Show notification
        if (errors.length === 0) {
          notification.success({
            message: "Bulk Upload Completed",
            description: `${successCount} users uploaded successfully!`,
          });
        } else if (successCount > 0) {
          notification.warning({
            message: "Bulk Upload Completed with Errors",
            description: `${successCount} succeeded, ${errors.length} failed.`,
          });
        } else {
          notification.error({
            message: "Bulk Upload Failed",
            description: `All ${errors.length} users failed to upload.`,
          });
        }
      },
      error: (error) => {
        console.error("CSV Parse Error:", error);
        notification.error({
          message: "CSV Parse Error",
          description: "Failed to parse CSV file. Please check the format.",
        });
        setBulkUploading(false);
      },
    });
  };

  // Close modal and reset state
  const handleClose = () => {
    setBulkFile(null);
    setBulkProgress(0);
    setBulkCurrent(0);
    setBulkTotal(0);
    setBulkErrors([]);
    setBulkSuccessCount(0);
    setShowBulkResults(false);
    onClose();
  };

  return (
    <>
      {/* Main Upload Modal - Hidden during upload, shown for file selection and results */}
      <Modal
        title="Bulk User Upload (CSV)"
        open={(visible && !bulkUploading) || showBulkResults} // Show for upload or results
        onCancel={handleClose}
        footer={null}
        width={800}
        closable={true}
        maskClosable={false}
      >
      {!showBulkResults ? (
        <>
          {/* File Upload Section */}
          <Alert
            message="CSV Format Requirements"
            description={
              <div>
                <p>
                  <strong>Your CSV must include these columns (in order):</strong>
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    marginTop: 10,
                  }}
                >
                  <div>• <strong>employeeId</strong> - Employee ID</div>
                  <div>• <strong>username</strong> - Full name</div>
                  <div>• <strong>email</strong> - Valid email</div>
                  <div>• <strong>company</strong> - Company name</div>
                  <div>• <strong>team</strong> - Team name</div>
                  <div>• <strong>level</strong> - User level</div>
                  <div>• <strong>manager</strong> - Manager name (optional)</div>
                  <div>• <strong>domain</strong> - User domain</div>
                  <div>• <strong>department</strong> - Department</div>
                  <div>• <strong>new_batch</strong> - true/false</div>
                </div>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Button
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
            style={{ marginBottom: 16, marginRight:16 }}
            size="large"
          >
            Download CSV Template
          </Button>

          <Upload
            accept=".csv"
            beforeUpload={(file) => {
              setBulkFile(file);
              return false;
            }}
            maxCount={1}
            onRemove={() => setBulkFile(null)}
            disabled={bulkUploading}
          >
            <Button icon={<UploadOutlined />} size="large" disabled={bulkUploading}>
              Select CSV File
            </Button>
          </Upload>

          {bulkFile && (
            <Alert
              message={`Selected file: ${bulkFile.name}`}
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          <Button
            type="primary"
            size="large"
            style={{ marginTop: 16, width: "100%" }}
            disabled={!bulkFile || bulkUploading}
            onClick={handleBulkUpload}
            loading={bulkUploading}
          >
            {bulkUploading ? "Processing..." : "Upload & Process"}
          </Button>
        </>
      ) : (
        <>
          {/* Results Section */}
          <Alert
            message="Upload Complete"
            description={`Successfully uploaded: ${bulkSuccessCount} | Failed: ${bulkErrors.length}`}
            type={bulkErrors.length === 0 ? "success" : "warning"}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {bulkSuccessCount > 0 && (
            <Alert
              message="Success"
              description={`${bulkSuccessCount} users have been added and welcome emails have been sent.`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {bulkErrors.length > 0 && (
            <>
              <h4 style={{ color: "#ff4d4f" }}>
                ⚠️ Errors ({bulkErrors.length}):
              </h4>
              <List
                size="small"
                bordered
                dataSource={bulkErrors}
                renderItem={(item) => (
                  <List.Item
                    style={{
                      background: item.row % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <Typography.Text strong style={{ color: "#ff4d4f" }}>
                        Row {item.row}:
                      </Typography.Text>
                      <br />
                      <Typography.Text style={{ fontSize: 13 }}>
                        📧 {item.email}
                      </Typography.Text>
                      <br />
                      <Typography.Text type="danger" style={{ fontSize: 12 }}>
                        ⚠️ {item.error}
                      </Typography.Text>
                    </div>
                  </List.Item>
                )}
                style={{
                  maxHeight: 300,
                  overflow: "auto",
                  marginBottom: 16,
                }}
              />
            </>
          )}

          <Button
            type="primary"
            size="large"
            style={{ width: "100%" }}
            onClick={handleClose}
          >
            Close
          </Button>
        </>
      )}
      </Modal>

      {/* Separate Progress Modal */}
      <UploadProgressModal
        visible={bulkUploading}
        bulkProgress={bulkProgress}
        bulkCurrent={bulkCurrent}
        bulkTotal={bulkTotal}
      />
    </>
  );
};

export default BulkUpload;